import React, { useContext, useRef, useState } from "react";
import { CircularProgress, Grid } from "@material-ui/core";
import { TextField } from "office-ui-fabric-react";
import { DefaultButton } from "@fluentui/react/lib/Button";
import { InstitutionContext } from "../../contexts/InstitutionContext";
import {
  Institution,
  InstitutionApi,
  InstitutionDto,
} from "../../api-clients/api";
import { getApiUrl } from "../../api-clients";
import { AppContext } from "../../contexts/AppContext";
import FormButtons from "../Shared/FormButtons";
import { useTranslation } from "react-i18next";
import { useRole } from "../../hooks/useRole";

export default function SchoolSettings() {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const { institution, institutionUpdated } = useContext(InstitutionContext);
  const { openSnackbar } = useContext(AppContext);
  const [saving, setSaving] = useState<boolean>(false);
  const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [institutionApi] = useState<InstitutionApi>(
    new InstitutionApi(getApiUrl())
  );
  const [institutionForm, setInstitutionForm] =
    useState<Institution | null>(institution);
  const canSave: boolean =
    institutionForm != null &&
    institutionForm.name != null &&
    institutionForm.city != null &&
    institutionForm.name.length > 0 &&
    institutionForm.city.length > 0;

  const save = (values: InstitutionDto) => {
    if (institution && institution.institutionId) {
      setSaving(true);
      institutionApi
        .update(institution.institutionId, values)
        .then(() => {
          setSaving(false);
          institutionUpdated();
        })
        .catch((er) => {
          console.log(er);
          setSaving(false);
        });
    }
  };

  const uploadLogo = (file: File) => {
    if (institution && institution.institutionId) {
      setUploadingLogo(true);
      institutionApi
        .uploadLogo(institution.institutionId, {
          data: file,
          fileName: file.name,
        })
        .then(() => {
          setUploadingLogo(false);
          openSnackbar(t("settings.logoUpdateSuccess"), "success");
          institutionUpdated();
        })
        .catch((er) => {
          console.log(er);
          setUploadingLogo(false);
          openSnackbar(t("common.errorOccurred"), "error");
        });
    }
  };

  const onLogoSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.substring(result.indexOf(",") + 1);
      setInstitutionForm((prev) =>
        prev ? ({ ...prev, logo: base64 } as Institution) : prev
      );
    };
    reader.readAsDataURL(file);
    uploadLogo(file);
    event.target.value = "";
  };

  return (
    <Grid>
      {institutionForm && (
        <>
          <Grid item xs={12} sm={6}>
            <div style={{ marginBottom: "16px" }}>
              {institutionForm.logo && (
                <div style={{ marginBottom: "8px" }}>
                  <img
                    src={`data:image/jpeg;base64,${institutionForm.logo}`}
                    alt={t("settings.logo")}
                    style={{ maxHeight: "80px", maxWidth: "240px" }}
                  />
                </div>
              )}
              {isAdmin && (
                <>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={onLogoSelected}
                  />
                  <DefaultButton
                    text={t("settings.changeLogo")}
                    iconProps={{ iconName: "Photo2Add" }}
                    disabled={uploadingLogo}
                    onClick={() => logoInputRef.current?.click()}
                  />
                  {uploadingLogo && (
                    <CircularProgress
                      size={20}
                      style={{ marginLeft: "12px", verticalAlign: "middle" }}
                    />
                  )}
                </>
              )}
            </div>
            <TextField
              label={t("settings.institutionName")}
              placeholder={t("settings.institutionName")}
              disabled={!isAdmin}
              required
              maxLength={200}
              value={institutionForm.name}
              onChange={(
                event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                newValue?: string
              ) =>
                setInstitutionForm({
                  ...institutionForm,
                  name: newValue,
                } as Institution)
              }
            />
            <TextField
              label={t("settings.city")}
              placeholder={t("settings.city")}
              disabled={!isAdmin}
              required
              maxLength={200}
              value={institutionForm.city}
              onChange={(
                event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                newValue?: string
              ) =>
                setInstitutionForm({
                  ...institutionForm,
                  city: newValue,
                } as Institution)
              }
            />
            <TextField
              label={t("settings.address")}
              placeholder={t("settings.address")}
              disabled={!isAdmin}
              maxLength={500}
              value={institutionForm.address}
              onChange={(
                event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                newValue?: string
              ) =>
                setInstitutionForm({
                  ...institutionForm,
                  address: newValue,
                } as Institution)
              }
            />
            <TextField
              label={t("settings.phone")}
              placeholder={t("settings.phone")}
              disabled={!isAdmin}
              maxLength={500}
              value={institutionForm.telephoneContact}
              onChange={(
                event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                newValue?: string
              ) =>
                setInstitutionForm({
                  ...institutionForm,
                  telephoneContact: newValue,
                } as Institution)
              }
            />
            <TextField
              label="E-Mail"
              placeholder="E-Mail"
              disabled={!isAdmin}
              maxLength={500}
              value={institutionForm.emailContact}
              onChange={(
                event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                newValue?: string
              ) =>
                setInstitutionForm({
                  ...institutionForm,
                  emailContact: newValue,
                } as Institution)
              }
            />
            <TextField
              label={t("settings.website")}
              placeholder={t("settings.website")}
              disabled={!isAdmin}
              maxLength={500}
              value={institutionForm.website}
              onChange={(
                event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                newValue?: string
              ) =>
                setInstitutionForm({
                  ...institutionForm,
                  website: newValue,
                } as Institution)
              }
            />
          </Grid>
          <Grid xs={12}>
            {isAdmin && (
              <FormButtons
                disabled={saving || !canSave}
                cancelButtonVisible={false}
                onConfirmClick={() => {
                  save(institutionForm);
                }}
              />
            )}
            {saving && (
              <CircularProgress
                size={24}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: -12,
                  marginLeft: -12,
                }}
              />
            )}
          </Grid>
        </>
      )}
    </Grid>
  );
}
